import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { LANGUAGE } from '@/utils/constant.utils';
import { Flag } from '../components/Flag';
import { Text } from '../components/Text';
import { ArrowIcon } from '@/components/SvgIcons/ArrowIcon';
import { CheckIcon } from '@/components/SvgIcons/CheckIcon';

interface LanguageSelectProps {
  containerClassName?: string;
  onLanguageChange?: (language: string) => void;
}

export const LanguageSelect = ({
  containerClassName,
  onLanguageChange,
}: LanguageSelectProps) => {
  const { i18n } = useTranslation('common');
  const [selectedLanguage, setSelectedLanguage] = useState(
    i18n.language.toUpperCase() || 'FR'
  );
  const [isOpen, setIsOpen] = useState(false);
  const [openUpwards, setOpenUpwards] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Calculate dropdown position when opening
  useEffect(() => {
    if (isOpen && selectRef.current) {
      const selectRect = selectRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = LANGUAGE.length * 50; // Approximate height per item
      const spaceBelow = viewportHeight - selectRect.bottom;
      const spaceAbove = selectRect.top;

      // Open upwards only if:
      // 1. Not enough space below AND
      // 2. Enough space above AND
      // 3. Opening upward wouldn't cause the dropdown to extend beyond the top
      const shouldOpenUpward =
        spaceBelow < dropdownHeight &&
        spaceAbove > dropdownHeight &&
        spaceAbove >= dropdownHeight;

      setOpenUpwards(shouldOpenUpward);
    }
  }, [isOpen]);

  const handleLanguageChange = (language: string) => {
    setSelectedLanguage(language);
    i18n.changeLanguage(language.toLowerCase());
    setIsOpen(false);
    if (onLanguageChange) {
      onLanguageChange(language);
    }
  };

  const handleToggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggleDropdown();
    } else if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const getCurrentLanguageData = () => {
    return (
      LANGUAGE.find((lang) => lang.lang === selectedLanguage) || LANGUAGE[0]
    );
  };

  const currentLang = getCurrentLanguageData();

  return (
    <div
      className={twMerge('relative z-50', containerClassName)}
      ref={dropdownRef}
    >
      {/* Select Display */}
      <div
        ref={selectRef}
        className="flex items-center gap-3 hover:cursor-pointer"
        onClick={handleToggleDropdown}
        onKeyDown={handleKeyDown}
      >
        <div className="flex items-center gap-2">
          <Flag flag={currentLang.flag} language={currentLang.lang} />
          <Text text={currentLang.lang === 'FR' ? 'Français' : 'English'} />
        </div>
        <ArrowIcon
          className={twMerge(
            'w-4 h-4 text-gray-900 transition-transform',
            isOpen && 'rotate-180'
          )}
        />
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className={twMerge(
            'absolute -left-1.25 w-full bg-white border border-gray-300 rounded-[5px] shadow-lg animate-in duration-200',
            openUpwards
              ? 'bottom-full mb-2 slide-in-from-bottom-2'
              : 'top-full mt-2 slide-in-from-top-2'
          )}
        >
          {LANGUAGE.map((item) => (
            <button
              key={item.lang}
              onClick={() => handleLanguageChange(item.lang)}
              className={twMerge(
                'w-full flex items-center gap-3 px-3.5 py-1.25 text-left hover:bg-gray-100 transition-colors first-of-type:pt-2.75 last-of-type:pb-2.75',
                selectedLanguage === item.lang && 'text-primary'
              )}
            >
              <div className="flex items-center gap-x-2">
                <Flag
                  flag={item.flag}
                  language={item.lang}
                  className="w-[20px] h-[16px]"
                />
                <Text
                  text={item.lang === 'FR' ? 'Français' : 'English'}
                  className="text-[14px]"
                />
              </div>
              {selectedLanguage === item.lang && (
                <CheckIcon className="w-4 h-4" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
